import React, {
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
} from "react";
import { FiPackage, FiMap, FiRefreshCw } from "react-icons/fi";
import AdminService from "src/services/admin.services";

interface OrderLocation {
  latitude: number;
  longitude: number;
  lastUpdated: string;
  accuracy?: number;
  speed?: number;
  heading?: number;
}

interface TrackingOrder {
  id: string;
  orderId: string;
  parcelId?: string;
  status:
    | "pending"
    | "assigned"
    | "picked_up"
    | "in_transit"
    | "delivered"
    | "cancelled";
  customerName: string;
  pickupAddress: string;
  deliveryAddress: string;
  assignedAgent?: {
    id: string;
    name: string;
    vehicleType: string;
  };
  currentLocation?: OrderLocation;
  priority: "low" | "medium" | "high";
  orderType: "food" | "parcel" | "grocery";
  // Add the actual location data structure from your API
  pickup_location?: {
    address: string;
    latitude: number;
    longitude: number;
  };
  receiver?: {
    name: string;
    phone: string;
    address: {
      address: string;
      latitude: number;
      longitude: number;
      place_id: string;
    };
  };
  // Food order specific fields
  items?: Array<{
    qty: number;
    meal: {
      _id: string;
      name: string;
      description: string;
      price: {
        amount: number;
        currency_code: string;
      };
      image_url?: string;
      restaurant_name?: string;
      location?: {
        Type: string;
        Coordinates: [number, number]; // [longitude, latitude]
      };
    };
  }>;
  delivery_location?: string;
}

interface OrderMapProps {
  orders: TrackingOrder[];
  centerLat?: number;
  centerLng?: number;
  zoom?: number;
}

declare global {
  interface Window {
    google: any;
    initMap: () => void;
  }
}

const OrderMap: React.FC<OrderMapProps> = ({
  orders,
  centerLat = 9.082, // Default to Abuja latitude
  centerLng = 7.3986, // Default to Abuja longitude (corrected)
  zoom = 10,
}) => {
  const adminService = new AdminService();
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const markersRef = useRef<any[]>([]);
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const mapIdRef = useRef<string>(
    `order-map-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
  );
  const [trackingData, setTrackingData] = useState<Record<string, any>>({});
  const [isTracking, setIsTracking] = useState(false);
  const [selectedOrderForTracking, setSelectedOrderForTracking] =
    useState<TrackingOrder | null>(null);
  const [trackingInterval, setTrackingInterval] =
    useState<NodeJS.Timeout | null>(null);
  const [markerUpdateCount, setMarkerUpdateCount] = useState(0); // Force re-renders
  const [routeLines, setRouteLines] = useState<
    Record<string, google.maps.Polyline>
  >({}); // Store route lines
  const [restaurantMarkers, setRestaurantMarkers] = useState<
    Record<string, google.maps.Marker>
  >({}); // Store restaurant markers
  const [destinationMarkers, setDestinationMarkers] = useState<
    Record<string, google.maps.Marker>
  >({}); // Store destination markers
  // Load Google Maps script with improved error handling
  useEffect(() => {
    if (!window.google) {
      const script = document.createElement("script");
      // Use environment variable for Google Maps API key
      const apiKey =
        process.env.REACT_APP_GOOGLE_MAPS_API_KEY ||
        "AIzaSyD845dpQ62RHqNW83JcyA5YKaRQ05UVl8I";

      if (!apiKey || apiKey === "YOUR_API_KEY_HERE") {
        console.error("❌ Google Maps API key not configured");
        setIsMapLoaded(true); // Trigger fallback view
        return;
      }

      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=geometry&callback=initMap`;
      script.async = true;
      script.defer = true;

      // Add error handling
      script.onerror = (error) => {
        console.error("❌ Failed to load Google Maps script:", error);
        setIsMapLoaded(true); // Trigger fallback view
      };

      // Add timeout for script loading
      const timeout = setTimeout(() => {
        if (!window.google) {
          console.warn(
            "⚠️ Google Maps script loading timeout - falling back to grid view"
          );
          setIsMapLoaded(true); // This will trigger the fallback grid
        }
      }, 10000); // 10 second timeout

      window.initMap = () => {
        clearTimeout(timeout);
        if (window.google && window.google.maps) {
          setIsMapLoaded(true);
        } else {
          console.error("❌ Google Maps loaded but API not available");
          setIsMapLoaded(true); // Still trigger fallback
        }
      };

      document.head.appendChild(script);
    } else {
      setIsMapLoaded(true);
    }
  }, []);

  // Initialize map when Google Maps is loaded
  useEffect(() => {
    if (
      isMapLoaded &&
      mapRef.current &&
      !mapInstanceRef.current &&
      window.google?.maps
    ) {
      try {
        const map = new window.google.maps.Map(mapRef.current, {
          center: { lat: centerLat, lng: centerLng },
          zoom: zoom,
          mapTypeId: window.google.maps.MapTypeId.ROADMAP,
          // Enhanced map styling for better visual appeal
          styles: [
            // Show all place names and labels
            {
              featureType: "administrative.locality",
              elementType: "labels.text",
              stylers: [{ visibility: "on" }],
            },
            {
              featureType: "administrative.neighborhood",
              elementType: "labels.text",
              stylers: [{ visibility: "on" }],
            },
            {
              featureType: "poi",
              elementType: "labels.text",
              stylers: [{ visibility: "on" }],
            },
            {
              featureType: "road",
              elementType: "labels.text",
              stylers: [{ visibility: "on" }],
            },
            // Enhanced road styling
            {
              featureType: "road.highway",
              elementType: "geometry",
              stylers: [{ color: "#ffffff" }, { weight: 2.5 }],
            },
            {
              featureType: "road.arterial",
              elementType: "geometry",
              stylers: [{ color: "#ffffff" }, { weight: 1.5 }],
            },
            {
              featureType: "road.local",
              elementType: "geometry",
              stylers: [{ color: "#f8f9fa" }, { weight: 1 }],
            },
            // Enhanced natural features
            {
              featureType: "landscape.natural",
              elementType: "geometry",
              stylers: [{ color: "#e8f5e8" }],
            },
            {
              featureType: "water",
              elementType: "geometry",
              stylers: [{ color: "#a8d8f0" }],
            },
            // Enhanced POI styling
            {
              featureType: "poi.business",
              elementType: "geometry",
              stylers: [{ color: "#f0f0f0" }],
            },
            {
              featureType: "poi.medical",
              elementType: "geometry",
              stylers: [{ color: "#ffe6e6" }],
            },
            {
              featureType: "poi.sports_complex",
              elementType: "geometry",
              stylers: [{ color: "#e6f7ff" }],
            },
            {
              featureType: "transit.station",
              elementType: "geometry",
              stylers: [{ color: "#fff2e6" }],
            },
          ],
          // Enhanced map options
          disableDefaultUI: false,
          zoomControl: true,
          mapTypeControl: true,
          scaleControl: true,
          streetViewControl: true,
          fullscreenControl: true,
          gestureHandling: "cooperative",
          // Better visual quality
          backgroundColor: "#f8f9fa",
          clickableIcons: true,
          draggable: true,
          scrollwheel: true,
        });

        mapInstanceRef.current = map;
        // Don't show markers by default - only show when tracking specific orders
        // addOrderMarkers(map); // Commented out - markers only appear when tracking
      } catch (error) {
        console.error("Error creating map:", error);
      }
    }
  }, [isMapLoaded, centerLat, centerLng, zoom]); // Removed 'orders' dependency to prevent unnecessary re-renders

  // Add pickup marker (restaurant)
  const addPickupMarker = (
    orderId: string,
    position: { lat: number; lng: number },
    address: string
  ) => {
    // Remove existing pickup marker if any
    if (restaurantMarkers[orderId]) {
      restaurantMarkers[orderId].setMap(null);
    }

    // Create pickup marker with restaurant icon
    const pickupMarker = new window.google.maps.Marker({
      position: position,
      map: mapInstanceRef.current,
      title: `Pickup - Restaurant Location for ${orderId}`,
      icon: {
        url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
          <svg width="32" height="32" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
            <circle cx="16" cy="16" r="14" fill="#dc2626" stroke="#b91c1c" stroke-width="3"/>
            <text x="16" y="20" text-anchor="middle" fill="white" font-size="12" font-family="Arial, sans-serif">
              🏪
            </text>
          </svg>
        `)}`,
        scaledSize: new window.google.maps.Size(32, 32),
        anchor: new window.google.maps.Point(16, 16),
      },
    });

    // Add info window for pickup
    const infoWindow = new window.google.maps.InfoWindow({
      content: `
        <div style="padding: 10px; min-width: 200px;">
          <h3 style="margin: 0 0 8px 0; color: #1f2937;">🏪 Pickup Location</h3>
          <p style="margin: 4px 0; color: #6b7280;"><strong>Order:</strong> ${orderId}</p>
          <p style="margin: 4px 0; color: #6b7280;"><strong>Address:</strong> ${
            address || "Not specified"
          }</p>
          <p style="margin: 4px 0; color: #6b7280;"><strong>Type:</strong> Restaurant/Pickup Point</p>
        </div>
      `,
    });

    pickupMarker.addListener("click", () => {
      infoWindow.open(mapInstanceRef.current, pickupMarker);
    });

    // Store pickup marker
    setRestaurantMarkers((prev) => ({
      ...prev,
      [orderId]: pickupMarker,
    }));
  };

  // Add destination marker (drop location)
  const addDestinationMarker = (
    orderId: string,
    position: { lat: number; lng: number },
    address: string
  ) => {
    // Remove existing destination marker if any
    if (destinationMarkers[orderId]) {
      destinationMarkers[orderId].setMap(null);
    }

    // Create destination marker with destination icon
    const destinationMarker = new window.google.maps.Marker({
      position: position,
      map: mapInstanceRef.current,
      title: `Destination - Delivery Location for ${orderId}`,
      icon: {
        url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
          <svg width="32" height="32" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
            <circle cx="16" cy="16" r="14" fill="#059669" stroke="#047857" stroke-width="3"/>
            <text x="16" y="20" text-anchor="middle" fill="white" font-size="12" font-family="Arial, sans-serif">
              🏠
            </text>
          </svg>
        `)}`,
        scaledSize: new window.google.maps.Size(32, 32),
        anchor: new window.google.maps.Point(16, 16),
      },
    });

    // Add info window for destination
    const infoWindow = new window.google.maps.InfoWindow({
      content: `
        <div style="padding: 10px; min-width: 200px;">
          <h3 style="margin: 0 0 8px 0; color: #1f2937;">🏠 Delivery Destination</h3>
          <p style="margin: 4px 0; color: #6b7280;"><strong>Order:</strong> ${orderId}</p>
          <p style="margin: 4px 0; color: #6b7280;"><strong>Address:</strong> ${
            address || "Not specified"
          }</p>
          <p style="margin: 4px 0; color: #6b7280;"><strong>Type:</strong> Drop Location</p>
        </div>
      `,
    });

    destinationMarker.addListener("click", () => {
      infoWindow.open(mapInstanceRef.current, destinationMarker);
    });

    // Store destination marker
    setDestinationMarkers((prev) => ({
      ...prev,
      [orderId]: destinationMarker,
    }));
  };

  // Add a single order marker (for tracking)
  const addSingleOrderMarker = (
    order: TrackingOrder,
    position: { lat: number; lng: number }
  ) => {
    // Clear any existing markers first
    markersRef.current.forEach((marker) => marker.setMap(null));
    markersRef.current = [];

    const marker = new window.google.maps.Marker({
      position: position,
      map: mapInstanceRef.current,
      title: `${order.orderId} - ${order.customerName}`,
      icon: {
        url: getOrderMarkerIcon(order),
        scaledSize: new window.google.maps.Size(40, 40), // Larger for better visibility
        anchor: new window.google.maps.Point(20, 20),
      },
    });

    // Store order ID in marker for easier lookup
    (marker as any).orderId = order.orderId;
    (marker as any).orderInternalId = order.id;

    // Add info window
    const infoWindow = new window.google.maps.InfoWindow({
      content: `
        <div style="padding: 10px; min-width: 200px;">
          <h3 style="margin: 0 0 8px 0; color: #1f2937;">${order.orderId}</h3>
          <p style="margin: 4px 0; color: #6b7280;"><strong>Customer:</strong> ${
            order.customerName
          }</p>
          <p style="margin: 4px 0; color: #6b7280;"><strong>Status:</strong> ${order.status.replace(
            "_",
            " "
          )}</p>
          <p style="margin: 4px 0; color: #6b7280;"><strong>Type:</strong> ${
            order.orderType
          }</p>
          <p style="margin: 4px 0; color: #6b7280;"><strong>Priority:</strong> ${
            order.priority
          }</p>
          ${(() => {
            const locations = getOrderLocations(order);
            let pickupInfo = "";
            let deliveryInfo = "";

            if (locations.pickup) {
              pickupInfo = `<p style="margin: 4px 0; color: #6b7280;"><strong>Pickup:</strong> ${locations.pickup.address}</p>`;
            }

            if (locations.delivery && locations.delivery.lat !== 0) {
              deliveryInfo = `<p style="margin: 4px 0; color: #6b7280;"><strong>Delivery:</strong> ${locations.delivery.address}</p>`;
            }

            return pickupInfo + deliveryInfo;
          })()}
          <button onclick="startContinuousTrackingOrder('${order.orderId}', '${
        order.parcelId || ""
      }')" style="background: #f59e0b; color: white; border: none; padding: 8px 16px; border-radius: 4px; cursor: pointer; margin-top: 8px;">
            🎯 Currently Tracking
          </button>
        </div>
      `,
    });

    marker.addListener("click", () => {
      infoWindow.open(mapInstanceRef.current, marker);
    });

    markersRef.current.push(marker);
  };

  // Add markers for orders (currently unused but kept for future use)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const addOrderMarkers = (map: any) => {
    // Clear existing markers
    markersRef.current.forEach((marker) => marker.setMap(null));
    markersRef.current = [];

    activeOrders.forEach((order, index) => {
      // For pending orders without location, we'll show them at a default position
      // In a real app, you might want to geocode the pickup address
      let position;
      let hasRealLocation = false;

      if (
        order.currentLocation &&
        typeof order.currentLocation.latitude === "number" &&
        typeof order.currentLocation.longitude === "number" &&
        !isNaN(order.currentLocation.latitude) &&
        !isNaN(order.currentLocation.longitude)
      ) {
        // Validate coordinates are within Nigeria bounds before using them
        const lat = order.currentLocation.latitude;
        const lng = order.currentLocation.longitude;

        if (lat >= 4.0 && lat <= 14.0 && lng >= 2.0 && lng <= 15.0) {
          position = {
            lat: lat,
            lng: lng,
          };
          hasRealLocation = true;
        } else {
          console.warn(
            `⚠️ Order ${order.orderId} has invalid coordinates (${lat}, ${lng}), using Abuja fallback`
          );
          position = { lat: 9.082, lng: 7.3986 };
          hasRealLocation = false;
        }
      } else {
        // Default position for pending orders (center of map)
        position = { lat: centerLat, lng: centerLng };
        hasRealLocation = false;
      }

      const marker = new window.google.maps.Marker({
        position: position,
        map: map,
        title: `${order.orderId} - ${order.customerName}`,
        icon: {
          url: getOrderMarkerIcon(order),
          scaledSize: new window.google.maps.Size(32, 32),
          anchor: new window.google.maps.Point(16, 16),
        },
      });

      // Add info window
      const infoWindow = new window.google.maps.InfoWindow({
        content: `
          <div style="padding: 10px; min-width: 200px;">
            <h3 style="margin: 0 0 8px 0; color: #1f2937;">${order.orderId}</h3>
            <p style="margin: 4px 0; color: #6b7280;"><strong>Customer:</strong> ${
              order.customerName
            }</p>
            <p style="margin: 4px 0; color: #6b7280;"><strong>Status:</strong> ${order.status.replace(
              "_",
              " "
            )}</p>
            <p style="margin: 4px 0; color: #6b7280;"><strong>Type:</strong> ${
              order.orderType
            }</p>
            <p style="margin: 4px 0; color: #6b7280;"><strong>Priority:</strong> ${
              order.priority
            }</p>
            ${(() => {
              const locations = getOrderLocations(order);
              let pickupInfo = "";
              let deliveryInfo = "";

              if (locations.pickup) {
                pickupInfo = `<p style="margin: 4px 0; color: #6b7280;"><strong>Pickup:</strong> ${locations.pickup.address}</p>`;
              }

              if (locations.delivery && locations.delivery.lat !== 0) {
                deliveryInfo = `<p style="margin: 4px 0; color: #6b7280;"><strong>Delivery:</strong> ${locations.delivery.address}</p>`;
              }

              return pickupInfo + deliveryInfo;
            })()}
            ${
              order.currentLocation?.speed
                ? `<p style="margin: 4px 0; color: #6b7280;"><strong>Speed:</strong> ${order.currentLocation.speed} km/h</p>`
                : ""
            }
            ${
              hasRealLocation
                ? `<p style="margin: 4px 0; color: #6b7280;"><strong>Updated:</strong> ${new Date(
                    order.currentLocation?.lastUpdated || new Date()
                  ).toLocaleTimeString()}</p>`
                : '<p style="margin: 4px 0; color: #6b7280;"><strong>Status:</strong> Pending - No location data yet</p>'
            }
            <button onclick="startContinuousTrackingOrder('${
              order.orderId
            }', '${
          order.parcelId || ""
        }')" style="background: #f59e0b; color: white; border: none; padding: 5px 10px; border-radius: 4px; cursor: pointer; margin-top: 8px;">
              ${
                hasRealLocation
                  ? "Start Continuous Tracking"
                  : "Track Order (Pending)"
              }
            </button>
          </div>
        `,
      });

      marker.addListener("click", () => {
        infoWindow.open(map, marker);
      });

      markersRef.current.push(marker);
    });

    // Fit map to show all markers
    if (markersRef.current.length > 0) {
      try {
        const bounds = new window.google.maps.LatLngBounds();
        markersRef.current.forEach((marker) => {
          bounds.extend(marker.getPosition());
        });
        map.fitBounds(bounds);
      } catch (error) {
        console.error("Error adjusting map bounds:", error);
      }
    }
  };

  // Get marker icon based on order status and priority
  const getOrderMarkerIcon = (order: TrackingOrder): string => {
    const baseColor = getOrderStatusColor(order);
    const priorityRing = getPriorityRingColor(order.priority);

    // For pending orders, add a pulsing effect
    const isPending = order.status === "pending";

    // Create SVG icon with status color and priority ring
    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
      <svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
        <circle cx="20" cy="20" r="18" fill="${baseColor}" stroke="white" stroke-width="2"/>
        <circle cx="20" cy="20" r="16" fill="${baseColor}" stroke="${priorityRing}" stroke-width="3"/>
        <text x="20" y="25" text-anchor="middle" fill="white" font-size="14" font-family="Arial, sans-serif">
          ${getOrderTypeIcon(order.orderType)}
        </text>
        ${
          isPending
            ? '<circle cx="20" cy="20" r="22" fill="none" stroke="#fbbf24" stroke-width="2" stroke-dasharray="5,5" opacity="0.6"/>'
            : ""
        }
      </svg>
    `)}`;
  };

  const getPriorityRingColor = (priority: string) => {
    switch (priority) {
      case "high":
        return "#ef4444"; // Red
      case "medium":
        return "#eab308"; // Yellow
      case "low":
        return "#10b981"; // Green
      default:
        return "#6b7280";
    }
  };

  const getOrderStatusColor = (order: TrackingOrder) => {
    switch (order.status) {
      case "in_transit":
        return "#8b5cf6"; // Purple - In transit
      case "picked_up":
        return "#eab308"; // Yellow - Picked up
      case "assigned":
        return "#3b82f6"; // Blue - Assigned
      case "pending":
        return "#6b7280"; // Gray - Pending
      default:
        return "#9ca3af"; // Default gray
    }
  };

  const getOrderTypeIcon = (type: string) => {
    switch (type) {
      case "food":
        return "🍕";
      case "parcel":
        return "📦";
      case "grocery":
        return "🛒";
      default:
        return "📋";
    }
  };

  // Priority border function (currently unused but kept for future use)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const getPriorityBorder = (priority: string) => {
    switch (priority) {
      case "high":
        return "ring-2 ring-red-400";
      case "medium":
        return "ring-2 ring-yellow-400";
      case "low":
        return "ring-2 ring-green-400";
      default:
        return "";
    }
  };

  // Real-time tracking function using the tracking APIs
  const startTracking = useCallback(
    async (orderId: string, parcelId?: string) => {
      try {
        setIsTracking(true);
        let data: any = null;

        if (parcelId) {
          // Track parcel using admin service
          data = await adminService.trackParcel(parcelId);
        } else {
          // Track order using admin service - use the actual order ID, not tracking ID
          data = await adminService.trackOrder(orderId);
        }

        // Validate API response
        if (
          data &&
          typeof data === "object" &&
          typeof data.latitude === "number" &&
          typeof data.longitude === "number"
        ) {
          // Update tracking data state
          setTrackingData((prev) => ({
            ...prev,
            [orderId]: {
              coordinates: data,
              lastUpdated: new Date().toISOString(),
            },
          }));

          // Update marker position if map is loaded and marker exists
          if (mapInstanceRef.current && markersRef.current.length > 0) {
            await updateMarkerPosition(orderId, data);
          } else {
            console.warn("⚠️ Map not ready or no markers to update");
          }
        } else {
          console.warn(
            `⚠️ [${new Date().toLocaleTimeString()}] Tracking failed or invalid data:`,
            data
          );

          // Set error state for this order
          setTrackingData((prev) => ({
            ...prev,
            [orderId]: {
              error: "No valid coordinates received",
              lastUpdated: new Date().toISOString(),
            },
          }));
        }
      } catch (error) {
        console.error(
          `❌ [${new Date().toLocaleTimeString()}] Error tracking order/parcel:`,
          error
        );

        // Set error state for this order
        setTrackingData((prev) => ({
          ...prev,
          [orderId]: {
            error: error instanceof Error ? error.message : "Unknown error",
            lastUpdated: new Date().toISOString(),
          },
        }));
      } finally {
        // Don't set isTracking to false for continuous tracking
        // Only set it to false if we're not in continuous tracking mode
        if (!trackingInterval) {
          setIsTracking(false);
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [adminService, trackingInterval]
  );

  // Start continuous tracking for selected order
  const startContinuousTracking = useCallback(
    async (order: TrackingOrder) => {
      // Clear any existing tracking interval
      if (trackingInterval) {
        clearInterval(trackingInterval);
      }

      setSelectedOrderForTracking(order);

      // Always add a marker for the selected order, even if no current location
      if (mapInstanceRef.current) {
        // Get the pickup location (starting point) immediately
        const orderLocations = getOrderLocations(order);

        let pickupPosition: { lat: number; lng: number } | null = null;
        let orderMarkerPosition: { lat: number; lng: number } | null = null;

        // Order marker should ALWAYS start at pickup location (starting point)
        if (orderLocations.pickup) {
          orderMarkerPosition = {
            lat: orderLocations.pickup.lat,
            lng: orderLocations.pickup.lng,
          };
        } else {
          // Fallback to geocoded pickup address
          orderMarkerPosition = await geocodeAddress(order.pickupAddress);
          if (!orderMarkerPosition) {
            orderMarkerPosition = {
              lat: centerLat - 0.01,
              lng: centerLng - 0.01,
            };
          }
        }

        // Get pickup location for route drawing
        if (orderLocations.pickup) {
          pickupPosition = {
            lat: orderLocations.pickup.lat,
            lng: orderLocations.pickup.lng,
          };
        } else {
          // Fallback to geocoded pickup address
          pickupPosition = await geocodeAddress(order.pickupAddress);
          if (!pickupPosition) {
            pickupPosition = { lat: centerLat - 0.01, lng: centerLng - 0.01 };
          }
        }

        // IMMEDIATELY center map on pickup location (starting point)
        if (pickupPosition) {
          mapInstanceRef.current.setCenter(pickupPosition);
          mapInstanceRef.current.setZoom(14); // Zoom in closer to see the route clearly
        }

        // Add marker for the selected order at the determined position
        addSingleOrderMarker(order, orderMarkerPosition);

        // Get delivery location (destination point)
        let destinationPosition: { lat: number; lng: number } | null = null;

        // Get pickup location (start point) - already got above
        if (!orderLocations.pickup && !pickupPosition) {
          pickupPosition = { lat: centerLat - 0.01, lng: centerLng - 0.01 };
        }

        // Get delivery location (destination point)
        if (orderLocations.delivery && orderLocations.delivery.lat !== 0) {
          destinationPosition = {
            lat: orderLocations.delivery.lat,
            lng: orderLocations.delivery.lng,
          };
        } else {
          // Fallback to geocoded delivery address
          destinationPosition = await geocodeAddress(order.deliveryAddress);
          if (!destinationPosition) {
            destinationPosition = {
              lat: centerLat + 0.01,
              lng: centerLng + 0.01,
            };
          }
        }

        // Draw the route line directly - simple and straightforward
        if (pickupPosition && destinationPosition) {
          // ONLY create the order marker at pickup location (starting point)
          addSingleOrderMarker(order, pickupPosition);

          // Draw the route line from pickup to destination
          drawRouteLine(order.orderId, pickupPosition, destinationPosition);

          // Add pickup marker (restaurant)
          addPickupMarker(order.orderId, pickupPosition, order.pickupAddress);

          // Add destination marker (drop location)
          addDestinationMarker(
            order.orderId,
            destinationPosition,
            order.deliveryAddress
          );
        }
      }

      // For pending orders, we can still track them to get updates
      // Start polling every 10 seconds for the selected order
      const interval = setInterval(async () => {
        // For food orders, use order.id (the actual _id), for parcels use order.orderId (tracking ID)
        const trackingId =
          order.orderType === "food" ? order.id : order.orderId;
        await startTracking(trackingId, order.parcelId);
      }, 10000); // 10 seconds

      setTrackingInterval(interval);
      setIsTracking(true);

      // If it's a pending order, start tracking immediately to get initial data
      if (order.status === "pending") {
        const trackingId =
          order.orderType === "food" ? order.id : order.orderId;
        startTracking(trackingId, order.parcelId);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [startTracking]
  );

  // Stop continuous tracking
  const stopContinuousTracking = () => {
    if (trackingInterval) {
      clearInterval(trackingInterval);
      setTrackingInterval(null);
    }
    setSelectedOrderForTracking(null);
    setIsTracking(false);

    // Clear all markers when tracking stops
    markersRef.current.forEach((marker) => marker.setMap(null));
    markersRef.current = [];

    // Clear all route lines
    Object.values(routeLines).forEach((line) => line.setMap(null));
    setRouteLines({});

    // Clear all restaurant markers
    Object.values(restaurantMarkers).forEach((marker) => marker.setMap(null));
    setRestaurantMarkers({});

    // Clear all destination markers
    Object.values(destinationMarkers).forEach((marker) => marker.setMap(null));
    setDestinationMarkers({});
  };

  // Force map refresh by recreating the map instance
  const forceMapRefresh = () => {
    if (mapRef.current && mapInstanceRef.current) {
      // Store current map state before destroying
      const currentCenter = mapInstanceRef.current.getCenter();
      const currentZoom = mapInstanceRef.current.getZoom();
      const currentPosition = {
        lat: currentCenter.lat(),
        lng: currentCenter.lng(),
      };

      // Clear all markers and route lines
      markersRef.current.forEach((marker) => marker.setMap(null));
      Object.values(routeLines).forEach((line) => line.setMap(null));
      Object.values(restaurantMarkers).forEach((marker) => marker.setMap(null));
      Object.values(destinationMarkers).forEach((marker) =>
        marker.setMap(null)
      );

      // Destroy current map
      mapInstanceRef.current = null;

      // Recreate map after a short delay
      setTimeout(() => {
        if (mapRef.current) {
          const newMap = new window.google.maps.Map(mapRef.current, {
            center: currentPosition, // Use preserved position instead of default
            zoom: currentZoom, // Use preserved zoom level
            mapTypeId: window.google.maps.MapTypeId.ROADMAP,
            styles: [
              {
                featureType: "administrative.locality",
                elementType: "labels.text",
                stylers: [{ visibility: "on" }],
              },
              {
                featureType: "administrative.neighborhood",
                elementType: "labels.text",
                stylers: [{ visibility: "on" }],
              },
              {
                featureType: "poi",
                elementType: "labels.text",
                stylers: [{ visibility: "on" }],
              },
              {
                featureType: "road",
                elementType: "labels.text",
                stylers: [{ visibility: "on" }],
              },
            ],
            disableDefaultUI: false,
            zoomControl: true,
            mapTypeControl: true,
            scaleControl: true,
            streetViewControl: true,
            fullscreenControl: true,
            gestureHandling: "cooperative",
            backgroundColor: "#f8f9fa",
            clickableIcons: true,
            draggable: true,
            scrollwheel: true,
          });

          mapInstanceRef.current = newMap;

          // Re-add marker if we have a selected order
          if (selectedOrderForTracking) {
            const position = {
              lat:
                selectedOrderForTracking.currentLocation?.latitude ||
                currentPosition.lat,
              lng:
                selectedOrderForTracking.currentLocation?.longitude ||
                currentPosition.lng,
            };
            addSingleOrderMarker(selectedOrderForTracking, position);
          }
        }
      }, 100);
    }
  };

  // Alternative: Force map update using Google Maps events (currently unused but kept for future use)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const forceMapUpdate = () => {
    if (mapInstanceRef.current) {
      // Trigger multiple events to force update
      window.google.maps.event.trigger(mapInstanceRef.current, "resize");
      window.google.maps.event.trigger(
        mapInstanceRef.current,
        "bounds_changed"
      );
      window.google.maps.event.trigger(
        mapInstanceRef.current,
        "center_changed"
      );

      // Force a repaint
      mapInstanceRef.current.setZoom(mapInstanceRef.current.getZoom());
    }
  };

  // Helper function to validate and correct coordinates
  const validateAndCorrectCoordinates = (
    lat: number,
    lng: number,
    orderId: string
  ): { lat: number; lng: number } | null => {
    // Check if coordinates are valid numbers
    if (
      typeof lat !== "number" ||
      typeof lng !== "number" ||
      isNaN(lat) ||
      isNaN(lng)
    ) {
      console.warn(
        `⚠️ Invalid coordinate types for order ${orderId}: lat=${lat}, lng=${lng}. Using Abuja fallback.`
      );
      return { lat: 9.082, lng: 7.3986 };
    }

    // Nigeria bounds: lat: 4.0 to 14.0, lng: 2.0 to 15.0
    if (lat >= 4.0 && lat <= 14.0 && lng >= 2.0 && lng <= 15.0) {
      return { lat, lng };
    } else {
      console.warn(
        `⚠️ Invalid coordinates for order ${orderId}: lat=${lat}, lng=${lng}. Using Abuja fallback.`
      );
      // Return Abuja coordinates as fallback
      return { lat: 9.082, lng: 7.3986 };
    }
  };

  // Helper function to get pickup and delivery locations for both food and parcel orders
  const getOrderLocations = (
    order: TrackingOrder
  ): {
    pickup: { lat: number; lng: number; address: string } | null;
    delivery: { lat: number; lng: number; address: string } | null;
  } => {
    if (order.orderType === "parcel") {
      // Parcel order: use pickup_location and receiver.address
      const pickup =
        order.pickup_location &&
        typeof order.pickup_location.latitude === "number" &&
        typeof order.pickup_location.longitude === "number"
          ? (() => {
              const lat = order.pickup_location.latitude;
              const lng = order.pickup_location.longitude;

              const validatedCoords = validateAndCorrectCoordinates(
                lat,
                lng,
                order.orderId
              );
              if (validatedCoords) {
                return {
                  lat: validatedCoords.lat,
                  lng: validatedCoords.lng,
                  address: order.pickup_location.address || "Pickup Location",
                };
              }
              return null;
            })()
          : null;

      const delivery =
        order.receiver?.address &&
        typeof order.receiver.address.latitude === "number" &&
        typeof order.receiver.address.longitude === "number"
          ? (() => {
              const lat = order.receiver.address.latitude;
              const lng = order.receiver.address.longitude;

              const validatedCoords = validateAndCorrectCoordinates(
                lat,
                lng,
                order.orderId
              );
              if (validatedCoords) {
                return {
                  lat: validatedCoords.lat,
                  lng: validatedCoords.lng,
                  address:
                    order.receiver.address.address || "Delivery Location",
                };
              }
              return null;
            })()
          : null;

      // If parcel order has no location data, generate temporary coordinates
      if (!pickup || !delivery) {
        // Generate unique coordinates based on order ID - use proper Nigeria coordinates
        const orderIndex = parseInt(order.orderId.slice(-1)) || 0;
        const baseLat = 9.082; // Abuja center latitude
        const baseLng = 7.3986; // Abuja center longitude (corrected)
        const offset = (orderIndex + 1) * 0.01; // Small offset for each order

        const tempPickup = {
          lat: baseLat + offset,
          lng: baseLng + offset,
          address: `Temporary Pickup ${order.orderId}`,
        };

        const tempDelivery = {
          lat: baseLat - offset,
          lng: baseLng - offset,
          address: `Temporary Delivery ${order.orderId}`,
        };

        return { pickup: tempPickup, delivery: tempDelivery };
      }

      return { pickup, delivery };
    } else {
      // Food order: use items[0].meal.location.Coordinates for pickup
      const pickup =
        order.items &&
        order.items.length > 0 &&
        order.items[0].meal.location &&
        Array.isArray(order.items[0].meal.location.Coordinates) &&
        order.items[0].meal.location.Coordinates.length >= 2 &&
        typeof order.items[0].meal.location.Coordinates[0] === "number" &&
        typeof order.items[0].meal.location.Coordinates[1] === "number"
          ? (() => {
              const coords = order.items[0].meal.location.Coordinates;

              const lat = coords[0];
              const lng = coords[1];

              const validatedCoords = validateAndCorrectCoordinates(
                lat,
                lng,
                order.orderId
              );
              if (validatedCoords) {
                return {
                  lat: validatedCoords.lat,
                  lng: validatedCoords.lng,
                  address:
                    order.items[0].meal.restaurant_name ||
                    "Restaurant Location",
                };
              }
              return null;
            })()
          : null;

      // For food orders, delivery location needs to be geocoded from deliveryAddress
      // We'll return null and handle geocoding in the main function
      const delivery = null;

      return { pickup, delivery };
    }
  };

  // Geocode address to get coordinates
  const geocodeAddress = async (
    address: string
  ): Promise<{ lat: number; lng: number } | null> => {
    if (!window.google || !window.google.maps) {
      console.warn("⚠️ Google Maps not loaded, cannot geocode address");
      return null;
    }

    try {
      const geocoder = new window.google.maps.Geocoder();

      return new Promise((resolve) => {
        geocoder.geocode({ address }, (results: any, status: any) => {
          if (status === "OK" && results && results[0]) {
            const location = results[0].geometry.location;
            const coordinates = {
              lat: location.lat(),
              lng: location.lng(),
            };
            resolve(coordinates);
          } else {
            console.warn(
              "⚠️ Geocoding failed for address:",
              address,
              "Status:",
              status
            );
            resolve(null);
          }
        });
      });
    } catch (error) {
      console.error("❌ Error geocoding address:", address, error);
      return null;
    }
  };

  // Create restaurant marker (pickup location) (currently unused but kept for future use)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const createRestaurantMarker = async (
    orderId: string,
    pickupAddress: string,
    fallbackPosition?: { lat: number; lng: number }
  ) => {
    // Remove existing restaurant marker if any
    if (restaurantMarkers[orderId]) {
      restaurantMarkers[orderId].setMap(null);
    }

    // Try to geocode the pickup address
    let position = fallbackPosition;
    if (pickupAddress) {
      const geocodedPosition = await geocodeAddress(pickupAddress);
      if (geocodedPosition) {
        position = geocodedPosition;
      }
    }

    // Use fallback position if geocoding fails
    if (!position) {
      position = { lat: centerLat - 0.01, lng: centerLng - 0.01 };
      console.warn("⚠️ Using fallback position for restaurant marker");
    }

    // Create restaurant marker with restaurant icon
    const restaurantMarker = new window.google.maps.Marker({
      position: position,
      map: mapInstanceRef.current,
      title: `Restaurant - Pickup Location for ${orderId}`,
      icon: {
        url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
          <svg width="32" height="32" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
            <circle cx="16" cy="16" r="14" fill="#dc2626" stroke="#b91c1c" stroke-width="3"/>
            <text x="16" y="20" text-anchor="middle" fill="white" font-size="12" font-family="Arial, sans-serif">
              🏪
            </text>
          </svg>
        `)}`,
        scaledSize: new window.google.maps.Size(32, 32),
        anchor: new window.google.maps.Point(16, 16),
      },
    });

    // Add info window for restaurant
    const infoWindow = new window.google.maps.InfoWindow({
      content: `
        <div style="padding: 10px; min-width: 200px;">
          <h3 style="margin: 0 0 8px 0; color: #1f2937;">🏪 Restaurant</h3>
          <p style="margin: 4px 0; color: #6b7280;"><strong>Pickup Location</strong></p>
          <p style="margin: 4px 0; color: #6b7280;"><strong>Address:</strong> ${
            pickupAddress || "Not specified"
          }</p>
          <p style="margin: 4px 0; color: #6b7280;"><strong>Order:</strong> ${orderId}</p>
          <p style="margin: 4px 0; color: #6b7280;"><strong>Type:</strong> Pickup Point</p>
        </div>
      `,
    });

    restaurantMarker.addListener("click", () => {
      infoWindow.open(mapInstanceRef.current, restaurantMarker);
    });

    // Store restaurant marker
    setRestaurantMarkers((prev) => ({
      ...prev,
      [orderId]: restaurantMarker,
    }));

    return position;
  };

  // Create destination marker (delivery location) (currently unused but kept for future use)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const createDestinationMarker = async (
    orderId: string,
    deliveryAddress: string,
    fallbackPosition?: { lat: number; lng: number }
  ) => {
    // Remove existing destination marker if any
    if (destinationMarkers[orderId]) {
      destinationMarkers[orderId].setMap(null);
    }

    // Try to geocode the delivery address
    let position = fallbackPosition;
    if (deliveryAddress) {
      const geocodedPosition = await geocodeAddress(deliveryAddress);
      if (geocodedPosition) {
        position = geocodedPosition;
      }
    }

    // Use fallback position if geocoding fails
    if (!position) {
      position = { lat: centerLat + 0.01, lng: centerLng + 0.01 };
      console.warn("⚠️ Using fallback position for destination marker");
    }

    // Create destination marker with destination icon
    const destinationMarker = new window.google.maps.Marker({
      position: position,
      map: mapInstanceRef.current,
      title: `Destination - Delivery Location for ${orderId}`,
      icon: {
        url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
          <svg width="32" height="32" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
            <circle cx="16" cy="16" r="14" fill="#059669" stroke="#047857" stroke-width="3"/>
            <text x="16" y="20" text-anchor="middle" fill="white" font-size="12" font-family="Arial, sans-serif">
              🏠
            </text>
          </svg>
        `)}`,
        scaledSize: new window.google.maps.Size(32, 32),
        anchor: new window.google.maps.Point(16, 16),
      },
    });

    // Add info window for destination
    const infoWindow = new window.google.maps.InfoWindow({
      content: `
        <div style="padding: 10px; min-width: 200px;">
          <h3 style="margin: 0 0 8px 0; color: #1f2937;">🏠 Destination</h3>
          <p style="margin: 4px 0; color: #6b7280;"><strong>Delivery Location</strong></p>
          <p style="margin: 4px 0; color: #6b7280;"><strong>Address:</strong> ${
            deliveryAddress || "Not specified"
          }</p>
          <p style="margin: 4px 0; color: #6b7280;"><strong>Order:</strong> ${orderId}</p>
          <p style="margin: 4px 0; color: #6b7280;"><strong>Type:</strong> Delivery Point</p>
        </div>
      `,
    });

    destinationMarker.addListener("click", () => {
      infoWindow.open(mapInstanceRef.current, destinationMarker);
    });

    // Store destination marker
    setDestinationMarkers((prev) => ({
      ...prev,
      [orderId]: destinationMarker,
    }));

    return position;
  };

  // Draw route line from start to current position
  const drawRouteLine = (
    orderId: string,
    startPosition: { lat: number; lng: number },
    currentPosition: { lat: number; lng: number }
  ) => {
    // Remove existing route line if any
    if (routeLines[orderId]) {
      routeLines[orderId].setMap(null);
    }

    // Validate coordinates
    if (
      !startPosition ||
      !currentPosition ||
      typeof startPosition.lat !== "number" ||
      typeof startPosition.lng !== "number" ||
      typeof currentPosition.lat !== "number" ||
      typeof currentPosition.lng !== "number"
    ) {
      console.error("❌ Invalid coordinates for route line:", {
        startPosition,
        currentPosition,
      });
      return;
    }

    // Create new route line - SIMPLE AND STRAIGHTFORWARD
    try {
      const routeLine = new window.google.maps.Polyline({
        path: [startPosition, currentPosition],
        geodesic: true,
        strokeColor: "#ff0000", // BRIGHT RED for maximum visibility
        strokeOpacity: 1.0, // Full opacity
        strokeWeight: 12, // VERY thick line
        map: mapInstanceRef.current,
        zIndex: 1000, // Ensure it's above other elements
      });

      // Store route line
      setRouteLines((prev) => ({
        ...prev,
        [orderId]: routeLine,
      }));
    } catch (error) {
      console.error("❌ Error creating route line:", error);
      return;
    }

    // Force map update to ensure route line is visible
    if (mapInstanceRef.current) {
      window.google.maps.event.trigger(mapInstanceRef.current, "resize");
    }
  };

  // Update marker position on the map
  const updateMarkerPosition = async (
    orderId: string,
    coordinates: { latitude: number; longitude: number }
  ) => {
    // Validate coordinates
    if (
      !coordinates ||
      typeof coordinates.latitude !== "number" ||
      typeof coordinates.longitude !== "number"
    ) {
      console.error("❌ Invalid coordinates provided:", coordinates);
      return;
    }

    // Find marker by stored orderId or orderInternalId
    const marker = markersRef.current.find((m) => {
      const storedOrderId = (m as any).orderId;
      const storedInternalId = (m as any).orderInternalId;
      return storedOrderId === orderId || storedInternalId === orderId;
    });

    if (marker && mapInstanceRef.current) {
      const newPosition = {
        lat: coordinates.latitude,
        lng: coordinates.longitude,
      };

      // Use Google Maps setPosition method for smoother updates
      marker.setPosition(newPosition);

      // Add bounce animation to show movement
      marker.setAnimation(window.google.maps.Animation.BOUNCE);
      setTimeout(() => marker.setAnimation(null), 1000);

      // Update the marker's title to show last updated time
      const timestamp = new Date().toLocaleTimeString();
      marker.setTitle(
        `${marker.getTitle().split(" - Updated:")[0]} - Updated: ${timestamp}`
      );

      // Smoothly pan to the new position if this is the currently tracked order
      if (
        selectedOrderForTracking &&
        (selectedOrderForTracking.orderId === orderId ||
          selectedOrderForTracking.id === orderId)
      ) {
        // Get current map center
        const currentCenter = mapInstanceRef.current?.getCenter();
        if (currentCenter) {
          const currentLat = currentCenter.lat();
          const currentLng = currentCenter.lng();

          // Only pan if the new position is significantly different
          const latDiff = Math.abs(currentLat - newPosition.lat);
          const lngDiff = Math.abs(currentLng - newPosition.lng);

          if (latDiff > 0.001 || lngDiff > 0.001) {
            // 0.001 degrees ≈ 111 meters
            mapInstanceRef.current?.panTo(newPosition);
          }
        }
      }

      // Draw route line from pickup location to current delivery position
      if (selectedOrderForTracking) {
        // Use the helper function to get pickup and delivery locations
        const orderLocations = getOrderLocations(selectedOrderForTracking);
        let pickupPosition: { lat: number; lng: number } | null = null;
        let destinationPosition: { lat: number; lng: number } | null = null;

        // Get pickup location (start point)
        if (orderLocations.pickup) {
          pickupPosition = {
            lat: orderLocations.pickup.lat,
            lng: orderLocations.pickup.lng,
          };
        } else {
          // Fallback to geocoded pickup address
          pickupPosition = await geocodeAddress(
            selectedOrderForTracking.pickupAddress
          );
          if (!pickupPosition) {
            pickupPosition = { lat: centerLat - 0.01, lng: centerLng - 0.01 };
          }
        }

        // Get delivery location (destination point)
        if (orderLocations.delivery && orderLocations.delivery.lat !== 0) {
          destinationPosition = {
            lat: orderLocations.delivery.lat,
            lng: orderLocations.delivery.lng,
          };
        } else {
          // Fallback to geocoded delivery address
          destinationPosition = await geocodeAddress(
            selectedOrderForTracking.deliveryAddress
          );
          if (!destinationPosition) {
            destinationPosition = {
              lat: centerLat + 0.01,
              lng: centerLng + 0.01,
            };
          }
        }

        // Draw route line directly - simple and straightforward
        if (pickupPosition && destinationPosition) {
          drawRouteLine(orderId, pickupPosition, destinationPosition);
        }
      }

      // Force React re-render
      setMarkerUpdateCount((prev) => prev + 1);
    } else {
      console.warn("⚠️ Marker not found for order:", orderId);
    }
  };

  // Start tracking all active orders (for manual refresh)
  const startTrackingAll = async () => {
    // Use the global activeOrders but filter for tracking (exclude pending)
    const trackableOrders = activeOrders.filter((order) =>
      ["assigned", "picked_up", "in_transit"].includes(order.status)
    );

    for (const order of trackableOrders) {
      // For food orders, use order.id (the actual _id), for parcels use order.orderId (tracking ID)
      const trackingId = order.orderType === "food" ? order.id : order.orderId;
      await startTracking(trackingId, order.parcelId);
      // Small delay between requests to avoid overwhelming the API
      await new Promise((resolve) => setTimeout(resolve, 500));
    }
  };

  // Add global function for tracking from info window
  useEffect(() => {
    (window as any).trackOrder = (orderId: string, parcelId: string) => {
      startTracking(orderId, parcelId);
    };

    // Add global function for continuous tracking from info window
    (window as any).startContinuousTrackingOrder = (
      orderId: string,
      parcelId: string
    ) => {
      // Find order by either orderId or id (for food orders)
      const order = orders.find(
        (o) => o.orderId === orderId || o.id === orderId
      );
      if (order) {
        startContinuousTracking(order);
      }
    };
  }, [orders, startTracking, startContinuousTracking]);

  // Cleanup tracking interval and route lines on unmount
  useEffect(() => {
    return () => {
      if (trackingInterval) {
        clearInterval(trackingInterval);
      }
      // Clear route lines and all markers
      Object.values(routeLines).forEach((line) => line.setMap(null));
      Object.values(restaurantMarkers).forEach((marker) => marker.setMap(null));
      Object.values(destinationMarkers).forEach((marker) =>
        marker.setMap(null)
      );
    };
  }, [trackingInterval, routeLines, restaurantMarkers, destinationMarkers]);

  // Get active orders for display - memoized to prevent unnecessary recalculations
  const activeOrders = useMemo(
    () =>
      orders.filter((order) => {
        // First filter by status
        const hasValidStatus = [
          "pending",
          "assigned",
          "picked_up",
          "in_transit",
        ].includes(order.status);

        if (!hasValidStatus) return false;

        // Additional filter: if order has currentLocation, validate coordinates
        if (
          order.currentLocation &&
          typeof order.currentLocation.latitude === "number" &&
          typeof order.currentLocation.longitude === "number"
        ) {
          const lat = order.currentLocation.latitude;
          const lng = order.currentLocation.longitude;

          // Only include orders with valid Nigeria coordinates
          if (lat >= 4.0 && lat <= 14.0 && lng >= 2.0 && lng <= 15.0) {
            return true;
          } else {
            console.warn(
              `⚠️ Filtering out order ${order.orderId} with invalid coordinates (${lat}, ${lng})`
            );
            return false;
          }
        }

        // Include orders without currentLocation (they'll use pickup location)
        return true;
      }),
    [orders]
  );

  return (
    <div className="bg-white rounded-xl shadow-sm border border-amber-200 p-6">
      <div className="mb-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-amber-900">
              🗺️ Enhanced Map View
            </h3>
            <p className="text-sm text-amber-700">
              Interactive map with place names, roads, and landmarks. Select an
              order to start tracking and see it on the map.
            </p>
          </div>
          <button
            onClick={startTrackingAll}
            disabled={isTracking}
            className="flex items-center gap-2 px-3 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 transition-colors"
          >
            <FiRefreshCw
              className={`w-4 h-4 ${isTracking ? "animate-spin" : ""}`}
            />
            {isTracking ? "Tracking..." : "Refresh All"}
          </button>
        </div>
      </div>

      {/* Google Maps Container */}
      <div className="relative bg-gray-100 rounded-lg h-96 overflow-hidden border-2 border-dashed border-gray-300">
        {!isMapLoaded ? (
          <div className="absolute inset-0 bg-gradient-to-br from-amber-50 to-blue-50 flex items-center justify-center">
            <div className="text-center text-gray-500">
              <FiMap className="w-16 h-16 mx-auto mb-2 text-gray-300" />
              <p className="text-sm">Loading Enhanced Google Maps...</p>
              <p className="text-xs">
                Please wait while the detailed map initializes
              </p>
              <p className="text-xs text-blue-500 mt-2">
                Loading map with place names, roads, and landmarks...
              </p>
              <div className="mt-4 p-3 bg-white rounded border max-h-48 overflow-y-auto">
                <p className="text-sm font-medium mb-2">
                  Orders Available: {activeOrders.length}
                </p>
                {activeOrders.length > 0 ? (
                  <div className="space-y-1 text-xs">
                    {activeOrders.slice(0, 5).map((order) => (
                      <div
                        key={order.id}
                        className="flex items-center gap-2 p-1 bg-gray-50 rounded"
                      >
                        <span className="font-medium">{order.orderId}</span>
                        <span className="text-gray-500">-</span>
                        <span className="text-gray-600">
                          {order.customerName}
                        </span>
                        <span
                          className={`px-1 py-0.5 rounded text-xs ${
                            order.orderType === "food"
                              ? "bg-orange-100 text-orange-700"
                              : order.orderType === "parcel"
                              ? "bg-blue-100 text-blue-700"
                              : "bg-green-100 text-green-700"
                          }`}
                        >
                          {order.orderType === "food" && "🍕"}
                          {order.orderType === "parcel" && "📦"}
                          {order.orderType === "grocery" && "🛒"}
                        </span>
                      </div>
                    ))}
                    {activeOrders.length > 5 && (
                      <p className="text-xs text-gray-500 text-center">
                        ... and {activeOrders.length - 5} more
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-xs text-gray-500">
                    No active orders to display
                  </p>
                )}
              </div>
            </div>
          </div>
        ) : // Show either Google Maps or fallback grid layout
        window.google ? (
          <div
            ref={mapRef}
            id={mapIdRef.current}
            className="w-full h-full"
            style={{ minHeight: "384px" }}
          />
        ) : (
          // Fallback: Show orders in a grid layout when Google Maps fails
          <div className="p-4 h-full overflow-y-auto">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {activeOrders.map((order) => (
                <div
                  key={order.id}
                  className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-lg">
                      {order.orderType === "food" && "🍕"}
                      {order.orderType === "parcel" && "📦"}
                      {order.orderType === "grocery" && "🛒"}
                    </span>
                    <span className="font-medium text-sm">{order.orderId}</span>
                  </div>
                  <p className="text-xs text-gray-600 mb-1">
                    {order.customerName}
                  </p>
                  <div className="flex items-center gap-2">
                    <span
                      className={`px-2 py-1 rounded text-xs ${
                        order.status === "pending"
                          ? "bg-gray-100 text-gray-700"
                          : order.status === "assigned"
                          ? "bg-blue-100 text-blue-700"
                          : order.status === "picked_up"
                          ? "bg-yellow-100 text-yellow-700"
                          : order.status === "in_transit"
                          ? "bg-purple-100 text-purple-700"
                          : "bg-green-100 text-green-700"
                      }`}
                    >
                      {order.status.replace("_", " ")}
                    </span>
                    <span
                      className={`px-2 py-1 rounded text-xs ${
                        order.priority === "high"
                          ? "bg-red-100 text-red-700"
                          : order.priority === "medium"
                          ? "bg-yellow-100 text-yellow-700"
                          : "bg-green-100 text-green-700"
                      }`}
                    >
                      {order.priority}
                    </span>
                  </div>
                </div>
              ))}
            </div>
            {activeOrders.length === 0 && (
              <div className="text-center text-gray-500 py-8">
                <FiPackage className="w-16 h-16 mx-auto mb-2 text-gray-300" />
                <p className="text-sm">No active orders to display</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Tracking Status Display */}
      {selectedOrderForTracking && (
        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium text-blue-900">
              🎯 Currently Tracking: {selectedOrderForTracking.orderId}
            </h4>
            <span className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded">
              {isTracking ? "🔄 Live" : "⏸️ Paused"}
            </span>
          </div>

          {trackingData[selectedOrderForTracking.orderId] && (
            <div className="text-xs text-blue-700 space-y-1">
              <div className="flex items-center gap-4">
                <span>
                  <strong>Lat:</strong>{" "}
                  {trackingData[
                    selectedOrderForTracking.orderId
                  ].coordinates?.latitude?.toFixed(6) || "N/A"}
                </span>
                <span>
                  <strong>Lng:</strong>{" "}
                  {trackingData[
                    selectedOrderForTracking.orderId
                  ].coordinates?.longitude?.toFixed(6) || "N/A"}
                </span>
                <span>
                  <strong>Last Update:</strong>{" "}
                  {new Date(
                    trackingData[selectedOrderForTracking.orderId].lastUpdated
                  ).toLocaleTimeString()}
                </span>
              </div>
              <div className="text-blue-600">
                💡 Marker position updates automatically every 10 seconds
              </div>
              <div className="text-green-600 font-medium">
                🔄 Updates: {markerUpdateCount} | Map should refresh
                automatically
              </div>
              <div className="text-amber-600 font-medium">
                🛣️ Route line shows path from restaurant to destination
              </div>
              <div className="text-red-600 font-medium">
                🏪 Red marker shows restaurant pickup location
              </div>
              <div className="text-green-600 font-medium">
                🏠 Green marker shows delivery destination
              </div>
            </div>
          )}

          <div className="mt-2 flex gap-2">
            <button
              onClick={() => stopContinuousTracking()}
              className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded hover:bg-red-200 transition-colors"
            >
              Stop Tracking
            </button>
            <button
              onClick={() => {
                if (selectedOrderForTracking) {
                  const trackingId =
                    selectedOrderForTracking.orderType === "food"
                      ? selectedOrderForTracking.id
                      : selectedOrderForTracking.orderId;
                  startTracking(trackingId, selectedOrderForTracking.parcelId);
                }
              }}
              className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded hover:bg-green-200 transition-colors"
            >
              Refresh Now
            </button>
            <button
              onClick={async () => {
                // Test marker movement by simulating a coordinate update
                if (
                  selectedOrderForTracking &&
                  trackingData[selectedOrderForTracking.orderId]
                ) {
                  const currentData =
                    trackingData[selectedOrderForTracking.orderId];
                  const testCoordinates = {
                    latitude:
                      (currentData.coordinates?.latitude || 9.082) +
                      (Math.random() - 0.5) * 0.001,
                    longitude:
                      (currentData.coordinates?.longitude || 7.3986) +
                      (Math.random() - 0.5) * 0.001,
                  };
                  await updateMarkerPosition(
                    selectedOrderForTracking.orderId,
                    testCoordinates
                  );
                }
              }}
              className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded hover:bg-purple-200 transition-colors"
            >
              Test Movement
            </button>
            <button
              onClick={() => forceMapRefresh()}
              className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded hover:bg-blue-200 transition-colors"
            >
              Force Map Refresh
            </button>
            <button
              onClick={() => {
                if (
                  selectedOrderForTracking &&
                  trackingData[selectedOrderForTracking.orderId]
                ) {
                  const currentData =
                    trackingData[selectedOrderForTracking.orderId];
                  const position = {
                    lat: currentData.coordinates?.latitude || 9.082,
                    lng: currentData.coordinates?.longitude || 7.3986,
                  };
                  mapInstanceRef.current?.panTo(position);
                }
              }}
              className="text-xs bg-indigo-100 text-indigo-700 px-2 py-1 rounded hover:bg-indigo-200 transition-colors"
            >
              Return to Order
            </button>
          </div>
        </div>
      )}

      {/* Selected Order Tracking */}
      {selectedOrderForTracking && (
        <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-medium text-amber-800">
              🎯 Currently Tracking: {selectedOrderForTracking.orderId}
            </h4>
            <button
              onClick={stopContinuousTracking}
              className="px-3 py-1 bg-red-100 text-red-700 rounded text-xs hover:bg-red-200 transition-colors"
            >
              Stop Tracking
            </button>
          </div>

          {/* Order Type Badge */}
          <div className="mb-3">
            <span
              className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                selectedOrderForTracking.orderType === "food"
                  ? "bg-orange-100 text-orange-800"
                  : selectedOrderForTracking.orderType === "parcel"
                  ? "bg-blue-100 text-blue-800"
                  : "bg-green-100 text-green-800"
              }`}
            >
              {selectedOrderForTracking.orderType === "food" && "🍕 Food Order"}
              {selectedOrderForTracking.orderType === "parcel" &&
                "📦 Parcel Delivery"}
              {selectedOrderForTracking.orderType === "grocery" && "🛒 Grocery"}
            </span>
          </div>

          {/* Delivery Progress */}
          <div className="mb-3">
            <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
              <span>Delivery Progress</span>
              <span className="font-medium">
                {selectedOrderForTracking.status.replace("_", " ")}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className={`h-2 rounded-full transition-all duration-300 ${
                  selectedOrderForTracking.status === "pending"
                    ? "w-0"
                    : selectedOrderForTracking.status === "assigned"
                    ? "w-1/4"
                    : selectedOrderForTracking.status === "picked_up"
                    ? "w-1/2"
                    : selectedOrderForTracking.status === "in_transit"
                    ? "w-3/4"
                    : selectedOrderForTracking.status === "delivered"
                    ? "w-full"
                    : "w-0"
                } ${
                  selectedOrderForTracking.status === "delivered"
                    ? "bg-green-500"
                    : selectedOrderForTracking.status === "in_transit"
                    ? "bg-blue-500"
                    : selectedOrderForTracking.status === "picked_up"
                    ? "bg-yellow-500"
                    : selectedOrderForTracking.status === "assigned"
                    ? "bg-orange-500"
                    : "bg-gray-500"
                }`}
              ></div>
            </div>
          </div>

          <div className="text-xs text-amber-700 space-y-1">
            <p>
              <strong>Customer:</strong> {selectedOrderForTracking.customerName}
            </p>
            <p>
              <strong>Pickup:</strong>{" "}
              {(() => {
                const locations = getOrderLocations(selectedOrderForTracking);
                return (
                  locations.pickup?.address ||
                  selectedOrderForTracking.pickupAddress
                );
              })()}
            </p>
            <p>
              <strong>Delivery:</strong>{" "}
              {(() => {
                const locations = getOrderLocations(selectedOrderForTracking);
                return (
                  locations.delivery?.address ||
                  selectedOrderForTracking.deliveryAddress
                );
              })()}
            </p>
            {selectedOrderForTracking.assignedAgent && (
              <p>
                <strong>Rider:</strong>{" "}
                {selectedOrderForTracking.assignedAgent.name} (
                {selectedOrderForTracking.assignedAgent.vehicleType})
              </p>
            )}
            {trackingData[selectedOrderForTracking.orderId] && (
              <div className="mt-2 p-2 bg-white rounded border">
                <p className="text-green-600 font-medium">📍 Live Location:</p>
                <p className="text-xs">
                  {trackingData[
                    selectedOrderForTracking.orderId
                  ].coordinates?.latitude?.toFixed(4)}
                  ,{" "}
                  {trackingData[
                    selectedOrderForTracking.orderId
                  ].coordinates?.longitude?.toFixed(4)}
                </p>
                <p className="text-xs text-gray-500">
                  Last updated:{" "}
                  {new Date(
                    trackingData[selectedOrderForTracking.orderId].lastUpdated
                  ).toLocaleTimeString()}
                </p>
              </div>
            )}
            {(() => {
              const locations = getOrderLocations(selectedOrderForTracking);
              return (
                <>
                  {locations.pickup && (
                    <div className="mt-2 p-2 bg-red-50 rounded border">
                      <p className="text-red-600 font-medium">
                        🏪 Pickup Location:
                      </p>
                      <p className="text-xs">
                        {locations.pickup.lat.toFixed(4)},{" "}
                        {locations.pickup.lng.toFixed(4)}
                      </p>
                      <p className="text-xs text-gray-500">
                        {locations.pickup.address}
                      </p>
                    </div>
                  )}
                  {locations.delivery && locations.delivery.lat !== 0 && (
                    <div className="mt-2 p-2 bg-green-50 rounded border">
                      <p className="text-green-600 font-medium">
                        🏠 Delivery Destination:
                      </p>
                      <p className="text-xs">
                        {locations.delivery.lat.toFixed(4)},{" "}
                        {locations.delivery.lng.toFixed(4)}
                      </p>
                      <p className="text-xs text-gray-500">
                        {locations.delivery.address}
                      </p>
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        </div>
      )}

      {/* Enhanced Map Legend */}
      <div className="mt-4 p-3 bg-gray-50 rounded-lg">
        <div className="text-xs font-medium text-gray-700 mb-2">
          🗺️ Enhanced Map Features
        </div>
        <div className="text-xs text-gray-600 mb-2">
          <strong>Active Routes:</strong> {Object.keys(routeLines).length}{" "}
          route(s) currently displayed
        </div>
        <div className="grid grid-cols-2 gap-2 text-xs">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
            <span className="text-gray-600">In Transit</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
            <span className="text-gray-600">Picked Up</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
            <span className="text-gray-600">Assigned</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-gray-500 rounded-full"></div>
            <span className="text-gray-600">Pending</span>
          </div>
        </div>
        <div className="mt-2 text-xs text-gray-500">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 border-2 border-amber-400 border-dashed rounded-full"></div>
            <span className="text-gray-600">
              Pending orders (no location data yet)
            </span>
          </div>
        </div>
        <div className="mt-2 text-xs text-gray-500">
          Ring colors indicate priority:{" "}
          <span className="text-red-500">Red (High)</span>,{" "}
          <span className="text-yellow-500">Yellow (Medium)</span>,{" "}
          <span className="text-green-500">Green (Low)</span>
        </div>
        <div className="mt-2 text-xs text-gray-500">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
            <span className="text-gray-600">
              📦 Order Marker (tracking position)
            </span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-red-500 rounded-full"></div>
            <span className="text-gray-600">
              🏪 Pickup Location (Restaurant)
            </span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
            <span className="text-gray-600">🏠 Drop Location (Customer)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-red-500 rounded-full"></div>
            <span className="text-gray-600">🛣️ Route Line (pickup → drop)</span>
          </div>
        </div>
        <div className="mt-2 pt-2 border-t border-gray-200">
          <p className="text-xs text-gray-600">
            <strong>🗺️ Enhanced Features:</strong> Place names, road labels,
            landmarks, natural features, and points of interest are now visible
            for better navigation. Real-time tracking shows the complete route
            from pickup to delivery.
          </p>
          {Object.keys(routeLines).length > 0 && (
            <div className="mt-2 p-2 bg-amber-50 rounded border">
              <p className="text-xs font-medium text-amber-800">
                🛣️ Active Route Lines:
              </p>
              {Object.keys(routeLines).map((orderId) => (
                <p key={orderId} className="text-xs text-amber-700">
                  Order: {orderId} - Route line visible
                </p>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Order List */}
      <div className="mt-4 p-3 bg-gray-50 rounded-lg">
        <h4 className="text-sm font-medium text-gray-700 mb-3">
          📋 All Orders on Map ({activeOrders.length})
        </h4>
        <div className="space-y-2 max-h-64 overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-amber-300 scrollbar-track-amber-100 hover:scrollbar-thumb-amber-400 border border-gray-200 rounded p-2">
          {activeOrders.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-2">
              No orders to display
            </p>
          ) : (
            activeOrders.map((order) => (
              <div
                key={order.id}
                className="flex items-center justify-between text-xs p-3 bg-white rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <div
                    className={`w-2 h-2 rounded-full ${getOrderStatusColor(
                      order
                    )}`}
                  ></div>
                  <span className="font-medium">{order.orderId}</span>
                  <span className="text-gray-500">-</span>
                  <span className="text-gray-600">{order.customerName}</span>
                  <div className="flex items-center gap-1">
                    <span
                      className={`px-1.5 py-0.5 rounded text-xs ${
                        order.orderType === "food"
                          ? "bg-orange-100 text-orange-700"
                          : order.orderType === "parcel"
                          ? "bg-blue-100 text-blue-700"
                          : "bg-green-100 text-green-700"
                      }`}
                    >
                      {order.orderType === "food" && "🍕"}
                      {order.orderType === "parcel" && "📦"}
                      {order.orderType === "grocery" && "🛒"}
                    </span>
                    <span className="text-gray-400 text-xs">
                      {order.status.replace("_", " ")}
                    </span>
                  </div>
                </div>
                <div className="flex gap-1">
                  {selectedOrderForTracking?.id === order.id ? (
                    <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs">
                      🎯 Tracking
                    </span>
                  ) : (
                    <button
                      onClick={() => startContinuousTracking(order)}
                      disabled={isTracking}
                      className="px-2 py-1 bg-amber-100 text-amber-700 rounded text-xs hover:bg-amber-200 disabled:opacity-50"
                    >
                      Start Tracking
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Currently Tracking */}
      {selectedOrderForTracking && (
        <div className="mt-4 p-3 bg-blue-50 rounded-lg">
          <h4 className="text-sm font-medium text-blue-700 mb-2">
            🎯 Currently Tracking Continuously
          </h4>
          <div className="flex items-center justify-between">
            <div>
              <span className="text-blue-600 font-medium">
                {selectedOrderForTracking.orderId}
              </span>
              <div className="text-xs text-blue-500">
                {selectedOrderForTracking.orderType === "food" &&
                  "🍕 Food Order"}
                {selectedOrderForTracking.orderType === "parcel" &&
                  "📦 Parcel Delivery"}
                {selectedOrderForTracking.orderType === "grocery" &&
                  "🛒 Grocery"}
                {" - "}
                {selectedOrderForTracking.status.replace("_", " ")}
              </div>
            </div>
            <div className="text-right">
              <div className="text-xs text-blue-600">🔄 Polling every 10s</div>
              <button
                onClick={stopContinuousTracking}
                className="px-3 py-1 bg-red-100 text-red-700 rounded text-xs hover:bg-red-200 transition-colors"
              >
                Stop Tracking
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tracking Status */}
      {Object.keys(trackingData).length > 0 && (
        <div className="mt-4 p-3 bg-green-50 rounded-lg">
          <h4 className="text-sm font-medium text-green-700 mb-2">
            🎯 Live Tracking Data (Real-time coordinates from your APIs)
          </h4>
          <div className="space-y-1 text-xs">
            {Object.entries(trackingData).map(([orderId, data]) => (
              <div
                key={orderId}
                className="flex justify-between items-center p-2 bg-white rounded border"
              >
                <div className="flex-1">
                  <span className="text-green-600 font-medium">{orderId}</span>
                  <div className="text-green-700">
                    📍 {data.coordinates?.latitude?.toFixed(4)},{" "}
                    {data.coordinates?.longitude?.toFixed(4)}
                  </div>
                </div>
                <div className="text-right">
                  <span className="text-green-500 text-xs">
                    ⏰ {new Date(data.lastUpdated).toLocaleTimeString()}
                  </span>
                  <div className="text-xs text-gray-500">
                    {data.coordinates ? "✅ Live" : "⏳ Waiting"}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-2 text-xs text-green-600">
            🔄 Polling endpoints every 10 seconds for selected orders
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderMap;
